import React from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, Card, CardContent, Progress } from "@lemonadetraining/web-ui";
import { getLevelInfo, type AthleteRadarProfile } from "@lemonadetraining/contracts";
import VariantAvatarImage from "@lemonadetraining/web-shared/components/VariantAvatarImage";

export interface AthleteCardProps {
  displayName: string;
  photoURL?: string | null;
  /** Optional axis-of-the-day or radar-derived persona title (e.g. "The Grinder") */
  title?: string;
  profile: AthleteRadarProfile | null;
  /** When true, the card renders the empty/new-athlete state. */
  isNewAthlete?: boolean;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

const AthleteCard: React.FC<AthleteCardProps> = ({
  displayName,
  photoURL,
  title,
  profile,
  isNewAthlete = false,
}) => {
  const navigate = useNavigate();
  // Level and progress from the XP curve, as the app's XP bar shows them (LT-2008).
  const info = getLevelInfo(profile?.xp?.total ?? 0);
  const level = info.level;
  const xpProgress = isNewAthlete ? 0 : info.progressPct * 100;
  const progressText = info.isMaxLevel
    ? `${info.totalXp.toLocaleString()} XP, top level`
    : `${info.xpInLevel.toLocaleString()} / ${info.xpForNextLevel.toLocaleString()} XP`;
  const nextText = info.isMaxLevel
    ? ""
    : `${info.xpToNextLevel.toLocaleString()} XP to level ${level + 1}`;

  const handleClick = () => {
    navigate("/dashboard/profile");
  };

  return (
    <Card
      data-testid="athlete-card"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className="cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={`${displayName}, level ${level}${title ? `, "${title}"` : ""}. ${
        isNewAthlete ? "Your journey starts with one rep." : `${progressText}. ${nextText}.`
      } Open your profile.`}
    >
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <VariantAvatarImage src={photoURL} alt="" />
            <AvatarFallback>{initialsFromName(displayName)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground" data-testid="athlete-card-level">
              Level {level} · {info.title}
            </span>
            <span className="font-medium">{title ?? displayName}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <Progress
            value={xpProgress}
            data-testid="athlete-card-xp"
            // LT-344: same boundary fix as IndicatorProgressBar — the
            // `--primary` XP fill is ~1.33:1 vs the muted track (fails WCAG
            // 1.4.11 3:1). A `--muted-foreground` border (≥3:1 vs track, page,
            // and the fill) makes the filled extent perceivable without
            // changing the brand `--primary`. The shadcn Progress Root merges
            // this className via cn(). Verified in lib/wcagContrast.test.ts.
            className="border border-muted-foreground"
            aria-label={
              isNewAthlete
                ? "0 XP. Your journey starts with one rep."
                : `${progressText}. ${nextText}`
            }
          />
          <span
            className="flex justify-between gap-2 text-xs text-muted-foreground"
            data-testid="athlete-card-xp-text"
          >
            {isNewAthlete ? (
              <span>Your journey starts with one rep</span>
            ) : (
              <>
                <span className="font-medium text-foreground tabular-nums">{progressText}</span>
                <span className="tabular-nums">{nextText}</span>
              </>
            )}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default AthleteCard;
